import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logout from './Logout';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { saveUserLanguage } from '../hooks/UserLanguage';
import EditIcon from '@mui/icons-material/Edit';
import { toggleEditMode } from '../redux/editSlice';
import { ListItemIcon, ListItemButton } from '@mui/material';
import { getUsersWithinMunicipality } from '../hooks/GetUserWithinMunicipality';
import { getUsersWithoutMunicipality } from '../hooks/GetUserWithoutMunicipality'; // Added import

const Navbar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const isAuthenticated = useIsAuthenticated();
  const authHeader = useAuthHeader() || '';

  const municipality = useSelector(
    (state: RootState) => state.municipality.selectedMunicipality
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const editMode = useSelector((state: RootState) => state.edit.editMode);

  const [municipalityRole, setMunicipalityRole] = useState<string>('viewer');
  const [isGlobalAdmin, setIsGlobalAdmin] = useState<boolean>(false);

  useEffect(() => {
    const fetchRoles = async () => {
      const username = localStorage.getItem('username');

      if (!isAuthenticated || !username) {
        setMunicipalityRole('viewer');
        setIsGlobalAdmin(false);
        return;
      }

      try {
        if (municipality?.id) {
          const withinResult = await getUsersWithinMunicipality(
            authHeader,
            dispatch
          );
          if (Array.isArray(withinResult)) {
            const currentUserData = withinResult.find(
              (u) => u.user.username === username
            );
            if (currentUserData) {
              setMunicipalityRole(currentUserData.role);
            } else {
              setMunicipalityRole('viewer');
            }
          }
        } else {
          setMunicipalityRole('viewer');
        }

        const withoutResult = await getUsersWithoutMunicipality(
          authHeader,
          dispatch
        );
        if (Array.isArray(withoutResult)) {
          const openUserData = withoutResult.find(
            (u) => u.user.username === username
          );
          if (openUserData && openUserData.role === 'admin') {
            setIsGlobalAdmin(true);
          } else {
            setIsGlobalAdmin(false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user roles for Navbar', error);
        setMunicipalityRole('viewer');
        setIsGlobalAdmin(false);
      }
    };

    fetchRoles();
  }, [isAuthenticated, municipality?.id, authHeader, dispatch]);

  const canSeeEditMode =
    isGlobalAdmin || ['editor', 'manager', 'admin'].includes(municipalityRole);
  const canEditMunicipality =
    isGlobalAdmin || ['manager', 'admin'].includes(municipalityRole);

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const changeLanguage = async (selectedLanguage: string) => {
    setLanguage(selectedLanguage);
    await i18n.changeLanguage(selectedLanguage);
    if (isAuthenticated) {
      const token = authHeader?.replace(/^Bearer\s+/, '');
      await saveUserLanguage(selectedLanguage, token);
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    void changeLanguage(event.target.value);
  };

  const handleLinkClick = () => {
    navigate('/');
  };

  const menuItems = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 0',
          borderBottom: '1px solid #ccc',
        }}
      >
        {['de', 'fr', 'en', 'nl'].map((lang) => (
          <Typography
            key={lang}
            sx={{
              cursor: 'pointer',
              fontWeight: language === lang ? 'bold' : 'normal',
              color: language === lang ? '#1976d2' : 'black',
              margin: '0 8px',
            }}
            onClick={() => {
              void changeLanguage(lang);
            }}
          >
            {lang.toUpperCase()}
          </Typography>
        ))}
      </Box>
      <List>
        {isAuthenticated && (
          <>
            <ListItem>
              <Logout />
            </ListItem>

            {canSeeEditMode && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => dispatch(toggleEditMode())}>
                  <ListItemIcon>
                    <EditIcon
                      sx={{ color: editMode ? '#1976d2' : 'inherit' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={editMode ? 'Stop Editing' : 'Enter Edit Mode'}
                  />
                </ListItemButton>
              </ListItem>
            )}

            {editMode && canSeeEditMode && (
              <>
                <ListItem>
                  <Link
                    to="/poi-form"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <ListItemText primary={t('navbar.addPOI')} />
                  </Link>
                </ListItem>
                {canEditMunicipality && (
                  <ListItem>
                    <Link
                      to="/municipality"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <ListItemText primary={t('navbar.municipality')} />
                    </Link>
                  </ListItem>
                )}
              </>
            )}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgcolor="#2b2d30"
      p={1.5}
      mb={0}
      boxShadow="0px 4px 4px rgba(0, 0, 0, 0.1)"
    >
      <Box
        onClick={handleLinkClick}
        style={{
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        {municipality?.logo && (
          <img
            src={municipality.logo || ''}
            alt="Municipality Logo"
            style={{ height: 40, marginRight: 12 }}
          />
        )}
        <Typography sx={{ fontWeight: 'bold', color: 'white' }}>
          {municipality
            ? 'Swiss Emergency Map - Municipality: ' + municipality.name
            : t('navbar.appName')}
        </Typography>
      </Box>
      {isMobile ? (
        <Box display="flex" alignItems="center">
          <IconButton onClick={toggleDrawer(true)} sx={{ color: 'white' }}>
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={toggleDrawer(false)} sx={{ color: 'black' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            {menuItems}
          </Drawer>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isAuthenticated && (
            <>
              {canSeeEditMode && (
                <IconButton
                  onClick={() => dispatch(toggleEditMode())}
                  sx={{ color: editMode ? '#1976d2' : 'white', marginRight: 2 }}
                >
                  <EditIcon />
                </IconButton>
              )}

              {editMode && canSeeEditMode && (
                <>
                  <Link
                    id={'poi-form-link'}
                    to="/poi-form"
                    style={{ textDecoration: 'none', marginRight: 24 }}
                  >
                    <Typography sx={{ color: 'white' }}>
                      {t('navbar.addPOI')}
                    </Typography>
                  </Link>
                  {canEditMunicipality && (
                    <Link
                      id={'municipality-link'}
                      to="/municipality"
                      style={{ textDecoration: 'none', marginRight: 24 }}
                    >
                      <Typography sx={{ color: 'white' }}>
                        {t('navbar.municipality')}
                      </Typography>
                    </Link>
                  )}
                </>
              )}
            </>
          )}
          <Select
            size="small"
            value={language}
            onChange={handleLanguageChange}
            sx={{
              color: 'white',
              border: 'none',
              borderRadius: 2,
              marginRight: 2,
              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              '.MuiSvgIcon-root': { color: 'white' },
            }}
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="de">Deutsch</MenuItem>
            <MenuItem value="fr">Français</MenuItem>
            <MenuItem value="nl">Nederlands</MenuItem>
          </Select>
          {isAuthenticated ? (
            <Logout />
          ) : (
            <Link
              id={'login-link'}
              to="/login"
              style={{ textDecoration: 'none', marginRight: 24 }}
            >
              <Typography sx={{ color: 'white', cursor: 'pointer' }}>
                Login
              </Typography>
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Navbar;
